"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import type { PostFrontmatter } from "@/lib/blog";

const POSTS_PER_PAGE_DESKTOP = 9;
const POSTS_PER_PAGE_MOBILE = 3;

interface BlogGridProps {
  posts: { frontmatter: PostFrontmatter; readingTime: number }[];
  locale: string;
  translations: {
    minRead: string;
    noPosts: string;
    all: string;
    viewingAll: string;
  };
}

export default function BlogGrid({ posts, locale, translations }: BlogGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const perPage = isMobile ? POSTS_PER_PAGE_MOBILE : POSTS_PER_PAGE_DESKTOP;

  const scrollToGrid = () => {
    const lenis = (window as unknown as Record<string, unknown>).__lenis as { scrollTo: (target: number, opts?: Record<string, unknown>) => void } | undefined;
    if (lenis) {
      lenis.scrollTo(0, { duration: 1.2 });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  // Collect all unique tags
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    posts.forEach((p) => p.frontmatter.tags.forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  }, [posts]);

  // Filter posts by tag
  const filteredPosts = useMemo(() => {
    if (!activeTag) return posts;
    return posts.filter((p) => p.frontmatter.tags.includes(activeTag));
  }, [posts, activeTag]);

  // Pagination — all posts stay in the rendered DOM so crawlers see every
  // post link in the raw HTML; the current page window is toggled via CSS
  // visibility rather than slicing the array.
  const totalPages = Math.ceil(filteredPosts.length / perPage);
  const pageStart = (page - 1) * perPage;

  const handleTagClick = (tag: string | null) => {
    setActiveTag(tag);
    setPage(1);
  };

  const dateLocale = locale === "de" ? "de-DE" : locale === "no" ? "nb-NO" : "en-GB";

  return (
    <>
      {/* Tag filter */}
      <div ref={gridRef} className="flex items-center gap-[12px] mb-[32px] lg:mb-[40px] scroll-mt-[130px]">
        <div className="relative">
          <div className={cn(
            "flex items-center rounded-full text-[13px] leading-[1] border transition-all duration-200",
            activeTag
              ? "bg-[#4a3290] border-[#4a3290] text-white"
              : "bg-transparent border-[#353353] text-foreground/90 hover:border-white/30"
          )}>
            <button
              onClick={() => setFilterOpen((o) => !o)}
              className="flex items-center gap-[8px] px-[16px] py-[9px] cursor-pointer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              {activeTag || translations.viewingAll}
            </button>
            {activeTag && (
              <button
                onClick={(e) => { e.stopPropagation(); handleTagClick(null); }}
                className="pr-[12px] pl-[4px] py-[9px] cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
              >
                <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            )}
          </div>

          {filterOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)} />
              <div data-lenis-prevent className="absolute top-[calc(100%+8px)] left-0 z-50 min-w-[220px] max-h-[320px] overflow-y-auto overscroll-contain scrollbar-hide bg-gradient-to-b from-[#121124] to-[#0e0821] border border-white/[0.08] rounded-[14px] p-[8px] shadow-[0_12px_40px_rgba(0,0,0,0.5)]">
                <button
                  onClick={() => { handleTagClick(null); setFilterOpen(false); }}
                  className={cn(
                    "w-full text-left px-[14px] py-[10px] rounded-[8px] text-[13px] transition-colors duration-150 cursor-pointer",
                    !activeTag ? "bg-[#4a3290] text-white" : "text-foreground/80 hover:bg-white/[0.05]"
                  )}
                >
                  {translations.all}
                </button>
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => { handleTagClick(tag); setFilterOpen(false); }}
                    className={cn(
                      "w-full text-left px-[14px] py-[10px] rounded-[8px] text-[13px] transition-colors duration-150 cursor-pointer",
                      activeTag === tag ? "bg-[#4a3290] text-white" : "text-foreground/80 hover:bg-white/[0.05]"
                    )}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Grid */}
      {filteredPosts.length === 0 ? (
        <p className="text-[15px] font-light text-foreground/40">{translations.noPosts}</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-[16px]">
          {filteredPosts.map((post, i) => {
            const { frontmatter, readingTime } = post;
            const isVisible = i >= pageStart && i < pageStart + perPage;
            const formattedDate = new Date(frontmatter.date).toLocaleDateString(
              dateLocale,
              { year: "numeric", month: "short", day: "numeric" }
            );

            return (
              <Link key={frontmatter.slug} href={`/blog/${frontmatter.slug}`} className={cn("block h-full", !isVisible && "hidden")}>
                <AnimatedCard
                  className="rounded-[18px] lg:rounded-[22px] h-full"
                  circleClassName="w-[120px] h-[120px] blur-[70px]"
                  animationDelay={Math.max(0, i - pageStart) * 0.07}
                >
                  <BorderWrapper
                    wrapperClass="rounded-[18px] lg:rounded-[22px] h-full"
                    className="rounded-[18px] lg:rounded-[22px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] p-[28px] lg:p-[36px] overflow-hidden relative flex flex-col"
                  >
                    <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />

                    <div className="flex flex-col flex-1 relative z-10">
                      <div className="flex items-center gap-[12px] mb-[20px]">
                        <p className="text-[12px] font-light text-foreground/35">{formattedDate}</p>
                        <span className="w-[3px] h-[3px] rounded-full bg-foreground/20" />
                        <p className="text-[12px] font-light text-foreground/35">{readingTime} {translations.minRead}</p>
                      </div>

                      <h2 className="text-[20px] lg:text-[24px] font-semibold leading-[1.25] text-white mb-[14px]">
                        {frontmatter.title}
                      </h2>

                      <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 mb-[24px]">
                        {frontmatter.description}
                      </p>

                      {frontmatter.tags.length > 0 && (
                        <div className="flex flex-wrap gap-[6px] mt-auto pt-[16px] border-t border-white/[0.06]">
                          {frontmatter.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-[10px] py-[4px] rounded-full text-[11px] font-light border border-white/10 text-foreground/40">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </BorderWrapper>
                </AnimatedCard>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination — carousel arrow style */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-[40px] gap-[15px]">
          <button
            onClick={() => { setPage((p) => Math.max(1, p - 1)); scrollToGrid(); }}
            disabled={page === 1}
            className="w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border border-white/5 focus:bg-theme focus:text-white flex items-center justify-center transition-colors"
            aria-label="Previous page"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>

          <span className="text-[14px] font-light text-foreground/50 min-w-[60px] text-center">
            {page} / {totalPages}
          </span>

          <button
            onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); scrollToGrid(); }}
            disabled={page === totalPages}
            className="w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border border-white/5 focus:bg-theme focus:text-white flex items-center justify-center transition-colors"
            aria-label="Next page"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      )}
    </>
  );
}
